/**
 * triggerEnginePure - 純粋関数版トリガーエンジン
 *
 * GameEngine用の状態ベース実装。
 * グローバルレジストリを使わず、state.triggers で管理。
 * React setterを使わず、新しい状態オブジェクトを返す。
 */

import {
  TRIGGER_TYPES,
  ACTIVATION_TYPES,
  TRIGGER_PRIORITIES,
} from '../triggerTypes';
import type { TriggerTypeValue, ActivationTypeValue, TriggerPriorityValue } from '../triggerTypes';
import { getFutureCardTriggers, hasFutureCardTrigger } from '../cardTriggers/futureCards';
import { getFireCardTriggers, hasFireCardTrigger } from '../cardTriggers/fireCards';
import { getWaterCardTriggers, hasWaterCardTrigger } from '../cardTriggers/waterCards';
import { getLightCardTriggers, hasLightCardTrigger } from '../cardTriggers/lightCards';
import { getDarkCardTriggers, hasDarkCardTrigger } from '../cardTriggers/darkCards';
import { getPrimitiveCardTriggers, hasPrimitiveCardTrigger } from '../cardTriggers/primitiveCards';
import { getNeutralCardTriggers, hasNeutralCardTrigger } from '../cardTriggers/neutralCards';
import { addLog, getPlayer, getOpponentNumber, type GameState, type TriggerEntry } from './GameState';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { TriggerDefinition } from '../../types/effect';

// ========================================
// 型定義
// ========================================

/** トリガー実行用コンテキスト */
export interface TriggerContext {
  card?: CardWithId | MonsterInstance | null;
  destroyedCard?: CardWithId | MonsterInstance | null;
  slotIndex?: number | null;
  monsterIndex?: number | null;
  attackerIndex?: number;
  targetIndex?: number;
  currentPlayer?: PlayerNumber;
  /** 攻撃時トリガーで蓄積されるダメージボーナス */
  accumulatedDamageBonus?: number;
  [key: string]: unknown;
}

/** 属性チェッカー */
interface AttributeCheck {
  has: (cardId: string) => boolean;
  get: (cardId: string) => TriggerDefinition[] | null;
}

// ========================================
// トリガー解析
// ========================================

/**
 * カードの効果テキストからトリガーを解析
 */
export function parseCardTriggers(card: CardWithId | MonsterInstance | null): TriggerDefinition[] {
  const triggers: TriggerDefinition[] = [];

  if (!card || !card.effect) {
    return triggers;
  }

  // カード固有のトリガー実装をチェック（属性ごと）
  const attributeChecks: AttributeCheck[] = [
    { has: hasFutureCardTrigger, get: getFutureCardTriggers },
    { has: hasFireCardTrigger, get: getFireCardTriggers },
    { has: hasWaterCardTrigger, get: getWaterCardTriggers },
    { has: hasLightCardTrigger, get: getLightCardTriggers },
    { has: hasDarkCardTrigger, get: getDarkCardTriggers },
    { has: hasPrimitiveCardTrigger, get: getPrimitiveCardTriggers },
    { has: hasNeutralCardTrigger, get: getNeutralCardTriggers },
  ];

  for (const check of attributeChecks) {
    if (card.id && check.has(card.id)) {
      const cardSpecificTriggers = check.get(card.id);
      if (cardSpecificTriggers) {
        return cardSpecificTriggers;
      }
    }
  }

  // カード固有の実装がない場合、汎用パターンマッチング
  const effectText = card.effect;

  // 【召喚時】パターン
  if (effectText.includes('【召喚時】') || effectText.includes('【このカードの召喚時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時効果',
      effect: null, // 汎用パターンでは効果未定義
    });
  }

  // 【破壊時】【自壊時】パターン
  if (effectText.includes('【破壊時】') || effectText.includes('【自壊時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時効果',
      effect: null,
    });
  }

  // 【自分エンドフェイズ時】パターン
  if (effectText.includes('【自分エンドフェイズ時】') || effectText.includes('【エンドフェイズ時】')) {
    triggers.push({
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時効果',
      effect: null,
    });
  }

  return triggers;
}

// ========================================
// トリガー登録・解除（純粋関数版）
// ========================================

/**
 * カードのトリガーを解析して登録
 */
export function registerCardTriggersPure(
  state: GameState,
  card: CardWithId | MonsterInstance | null,
  owner: PlayerNumber,
  slotIndex: number | null = null
): GameState {
  if (!card || !card.uniqueId) {
    return state;
  }

  const triggers = parseCardTriggers(card);

  if (triggers.length === 0) {
    return state;
  }

  const newTriggers: Record<string, TriggerEntry[]> = { ...state.triggers };

  triggers.forEach((trigger) => {
    const triggerType = trigger.type;

    // トリガータイプの配列を取得または作成
    const typeArray: TriggerEntry[] = newTriggers[triggerType] ? [...newTriggers[triggerType]] : [];

    // 重複チェック
    const existingIndex = typeArray.findIndex((t) => t.cardId === card.uniqueId);

    const triggerEntry: TriggerEntry = {
      cardId: card.uniqueId,
      cardName: card.name,
      triggerType,
      owner,
      slotIndex,
      priority: trigger.priority || TRIGGER_PRIORITIES.NORMAL,
      activationType: trigger.activationType || ACTIVATION_TYPES.AUTOMATIC,
      condition: trigger.condition as ((context: unknown) => boolean) | undefined,
      effect: trigger.effect as ((context: unknown) => void) | undefined,
      description: trigger.description,
      usedThisTurn: false,
      targetSelf: trigger.targetSelf,
    };

    if (existingIndex !== -1) {
      typeArray[existingIndex] = triggerEntry;
    } else {
      typeArray.push(triggerEntry);
    }

    newTriggers[triggerType] = typeArray;
  });

  return {
    ...state,
    triggers: newTriggers,
  };
}

/**
 * カードのトリガーをすべて解除
 */
export function unregisterCardTriggersPure(state: GameState, cardId: string): GameState {
  const newTriggers: Record<string, TriggerEntry[]> = {};

  Object.keys(state.triggers).forEach((triggerType) => {
    const filtered = state.triggers[triggerType].filter((t) => t.cardId !== cardId);
    if (filtered.length > 0) {
      newTriggers[triggerType] = filtered;
    }
  });

  return {
    ...state,
    triggers: newTriggers,
  };
}

/**
 * すべてのトリガーをクリア
 */
export function clearAllTriggersPure(state: GameState): GameState {
  return {
    ...state,
    triggers: {},
  };
}

// ========================================
// トリガー発火（純粋関数版）
// ========================================

/**
 * 特定のトリガータイプを発火（自動発動のみ）
 */
export function fireTriggerPure(
  state: GameState,
  triggerType: TriggerTypeValue,
  context: TriggerContext = {}
): GameState {
  const triggers = state.triggers[triggerType];

  if (!triggers || triggers.length === 0) {
    return state;
  }

  // 自動発動のみをフィルター
  let automaticTriggers = triggers.filter((trigger) => {
    return trigger.activationType === ACTIVATION_TYPES.AUTOMATIC;
  });

  // ON_SUMMONトリガーは召喚されたカード自身のトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_SUMMON && context.card) {
    const summonedCard = context.card;
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === summonedCard.uniqueId;
    });
  }

  // ON_DESTROY_SELFトリガーは破壊されたカード自身のトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_DESTROY_SELF && context.destroyedCard) {
    const destroyedCard = context.destroyedCard;
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.cardId === destroyedCard.uniqueId;
    });
  }

  // フェイズ関連の「_SELF」トリガーはオーナーがターンプレイヤーの場合のみ発動
  const phaseSelfTriggers: TriggerTypeValue[] = [
    TRIGGER_TYPES.ON_TURN_START_SELF,
    TRIGGER_TYPES.ON_DRAW_PHASE_SELF,
    TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
    TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF,
    TRIGGER_TYPES.ON_END_PHASE_SELF,
  ];

  if (phaseSelfTriggers.includes(triggerType)) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === state.currentPlayer;
    });
  }

  // フェイズ関連の「_OPPONENT」トリガー
  if (triggerType === TRIGGER_TYPES.ON_OPPONENT_END_PHASE) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner !== state.currentPlayer;
    });
  }

  // ON_OPPONENT_DRAW: 相手がドローした時（相手視点で発火）
  // Note: このトリガーは currentPlayer が「ドローした側」で呼び出される
  // トリガー所有者が currentPlayer と異なる場合に発火
  if (triggerType === TRIGGER_TYPES.ON_OPPONENT_DRAW) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner !== state.currentPlayer;
    });
  }

  // ON_SELF_MAGIC_ACTIVATED: 自分が魔法を発動した時
  // トリガー所有者が currentPlayer と同じ場合に発火
  if (triggerType === TRIGGER_TYPES.ON_SELF_MAGIC_ACTIVATED) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner === state.currentPlayer;
    });
  }

  // ON_ATTACK: 【自攻撃時】このモンスター自身が攻撃した時のみ発火
  // targetSelf: false の場合は自分の全モンスターの攻撃で発火（【自分の〇〇モンスター攻撃時】用）
  if (triggerType === TRIGGER_TYPES.ON_ATTACK) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      if (trigger.owner !== state.currentPlayer) return false;
      // targetSelf: false の場合は自分の全モンスターの攻撃で発火
      if (trigger.targetSelf === false) return true;
      // デフォルト（targetSelf未指定 or true）は自身の攻撃のみ
      return trigger.slotIndex === context.attackerIndex;
    });
  }

  // ON_ATTACKED: 【被攻撃時】このモンスター自身が攻撃された時のみ発火
  // 攻撃対象自身のトリガーのみ発火
  if (triggerType === TRIGGER_TYPES.ON_ATTACKED) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner !== state.currentPlayer &&
             trigger.slotIndex === context.targetIndex;
    });
  }

  // ON_OPPONENT_ATTACK: 相手のモンスターが攻撃した時
  // トリガー所有者が currentPlayer と異なる場合に発火
  if (triggerType === TRIGGER_TYPES.ON_OPPONENT_ATTACK) {
    automaticTriggers = automaticTriggers.filter((trigger) => {
      return trigger.owner !== state.currentPlayer;
    });
  }

  if (automaticTriggers.length === 0) {
    return state;
  }

  // 優先度順にソート
  const sortedTriggers = sortTriggersByPriority(automaticTriggers, state.currentPlayer);

  // 各トリガーを実行
  let newState = state;

  sortedTriggers.forEach((trigger) => {
    try {
      // トリガーを所有するカードをフィールドから検索
      const ownerField = trigger.owner === 1 ? newState.p1.field : newState.p2.field;
      const triggerCard = ownerField
        ? ownerField.find((m) => m && m.uniqueId === trigger.cardId)
        : null;

      // トリガー実行用のコンテキスト
      // currentPlayerをトリガーのオーナーに切り替え（ON_OPPONENT_END_PHASE等で正しいフィールドを参照するため）
      const triggerContext = {
        ...context,
        card: triggerCard || context.card || context.destroyedCard,
        slotIndex: trigger.slotIndex ?? context.slotIndex,
        currentPlayer: trigger.owner,
      };

      // 条件チェック
      if (trigger.condition && !trigger.condition(triggerContext)) {
        return;
      }

      // 効果実行（純粋関数版）
      // effectが関数として定義されている場合は実行
      // 汎用パターンでeffect: nullの場合もログは出力
      newState = executeTriggerEffectPure(newState, trigger, triggerContext);
    } catch (error) {
      console.error(`トリガー実行エラー: ${trigger.cardName}`, error);
      newState = addLog(newState, `${trigger.cardName}の効果実行中にエラーが発生しました`, 'damage');
    }
  });

  return newState;
}

/**
 * トリガー効果を純粋関数として実行
 * contextアダプターを使用してtrigger.effectを実行
 */
function executeTriggerEffectPure(
  state: GameState,
  trigger: TriggerEntry,
  triggerContext: TriggerContext
): GameState {
  let newState = addLog(state, `${trigger.cardName || 'カード'}の${trigger.description || '効果'}を発動！`, 'info');

  // trigger.effectが関数として定義されている場合
  if (typeof trigger.effect === 'function') {
    try {
      const { createPureContext } = require('./contextAdapter');

      // contextアダプターを作成
      const context = createPureContext(newState, {
        effectOwner: trigger.owner,
        card: triggerContext.card,
        slotIndex: triggerContext.slotIndex,
        destroyedCard: triggerContext.destroyedCard,
        monsterIndex: triggerContext.slotIndex,
        attacker: triggerContext.attacker,
        target: triggerContext.target,
        attackerIndex: triggerContext.attackerIndex,
        targetIndex: triggerContext.targetIndex,
        leavingCard: triggerContext.leavingCard,
        destroyedTarget: triggerContext.destroyedTarget,
        phaseCard: triggerContext.phaseCard,
      });

      // トリガー効果を実行
      trigger.effect(context);

      // 更新されたstateを取得
      newState = context.getState();
    } catch (error) {
      console.warn('Trigger effect execution error:', error);
      newState = addLog(newState, '(トリガー効果実行エラー)', 'info');
    }
  }

  return newState;
}

/**
 * トリガーを優先度順にソート
 */
function sortTriggersByPriority(triggers: TriggerEntry[], currentPlayer: PlayerNumber): TriggerEntry[] {
  return [...triggers].sort((a, b) => {
    // 優先度が高い順（デフォルトはNORMAL = 0）
    const aPriority = a.priority ?? TRIGGER_PRIORITIES.NORMAL;
    const bPriority = b.priority ?? TRIGGER_PRIORITIES.NORMAL;
    if (bPriority !== aPriority) {
      return bPriority - aPriority;
    }

    // オーナーがターンプレイヤーを先に
    if (a.owner === currentPlayer && b.owner !== currentPlayer) {
      return -1;
    }
    if (b.owner === currentPlayer && a.owner !== currentPlayer) {
      return 1;
    }

    return 0;
  });
}

// ========================================
// ターン管理（純粋関数版）
// ========================================

/**
 * ターン終了時に使用済みフラグをリセット
 */
export function resetTurnFlagsPure(state: GameState): GameState {
  const newTriggers: Record<string, TriggerEntry[]> = {};

  Object.keys(state.triggers).forEach((triggerType) => {
    newTriggers[triggerType] = state.triggers[triggerType].map((trigger) => ({
      ...trigger,
      usedThisTurn: false,
    }));
  });

  return {
    ...state,
    triggers: newTriggers,
  };
}

// ========================================
// ユーティリティ
// ========================================

/**
 * カードの登録済みトリガーを取得
 */
export function getCardTriggersPure(state: GameState, cardId: string): TriggerEntry[] {
  const result: TriggerEntry[] = [];

  Object.keys(state.triggers).forEach((triggerType) => {
    state.triggers[triggerType].forEach((trigger) => {
      if (trigger.cardId === cardId) {
        result.push(trigger);
      }
    });
  });

  return result;
}

/**
 * トリガー統計を取得
 */
export function getTriggerStatsPure(state: GameState): Record<string, number> {
  const stats: Record<string, number> = {};

  Object.keys(state.triggers).forEach((triggerType) => {
    if (state.triggers[triggerType].length > 0) {
      stats[triggerType] = state.triggers[triggerType].length;
    }
  });

  return stats;
}
